<template>
  <div class="destination-search-widget">
    <!-- Simple Search Input with Icons -->
    <div class="input-container">
      <div class="input-row">
        <!-- Search Input -->
        <div class="search-input-wrapper">
          <input
            v-model="searchQuery"
            @input="handleSearch"
            @focus="showSuggestions = true"
            @keydown.esc="closeSuggestions"
            type="text"
            placeholder="Zoek bestemmingen..."
            class="search-input"
          />
          <svg class="search-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          
          <!-- Loading indicator -->
          <div v-if="loading" class="loading-indicator">
            <div class="loading-spinner"></div>
          </div>
        </div>
        
        <!-- Action Buttons -->
        <button
          @click="showMapModal = true"
          class="action-button map-button"
          title="Toon op wereldkaart"
        >
          <svg class="button-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-1.447-.894L15 4m0 13V4m0 0L9 7" />
          </svg>
        </button>
        
        <button
          @click="showListModal = true"
          class="action-button list-button"
          title="Bekijk alle bestemmingen"
        >
          <svg class="button-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
          </svg>
        </button>
      </div>
      
      <!-- Autocomplete Suggestions (Inline) -->
      <div
        v-if="showSuggestions && filteredResults.length > 0"
        class="suggestions-dropdown"
      >
        <div
          v-for="result in filteredResults.slice(0, 8)"
          :key="result.type + '-' + (result.id || result.name)"
          @click="selectResult(result)"
          class="suggestion-item"
        >
          <!-- Continent Result -->
          <template v-if="result.type === 'continent'">
            <span class="result-flag">{{ result.flag }}</span>
            <div class="result-content">
              <div class="result-name">{{ result.name }}</div>
              <div class="result-subtitle">{{ result.vacationCount }} landen - {{ result.description }}</div>
            </div>
            <div class="result-badge continent-badge">Continent</div>
          </template>
          
          <!-- Country Result -->
          <template v-else-if="result.type === 'country'">
            <span class="result-flag">{{ result.flag }}</span>
            <div class="result-content">
              <div class="result-name">{{ result.name }}</div>
              <div class="result-subtitle">{{ result.vacationCount }} vakanties - Klik om te bekijken</div>
            </div>
            <div class="result-badge country-badge">Land</div>
          </template>
          
          <!-- Vacation Result -->
          <template v-else-if="result.type === 'vacation'">
            <div class="result-image-wrapper">
              <img 
                v-if="result.image" 
                :src="result.image" 
                :alt="result.name"
                class="result-image"
                @error="$event.target.style.display='none'"
              />
              <div v-else class="result-image-placeholder">
                <svg class="placeholder-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
            </div>
            <div class="result-content">
              <div class="result-name">{{ result.name }}</div>
              <div class="result-subtitle">{{ result.title }}</div>
              <div class="result-meta">
                <span v-if="result.country" class="country-info">{{ result.country.flag }} {{ result.country.name }}</span>
                <div class="result-badge vacation-badge">Vakantie</div>
                <span class="click-hint">Klik om te openen</span>
              </div>
            </div>
          </template>
          
          <svg class="result-arrow" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </div>
        
        <!-- Show more button -->
        <div v-if="filteredResults.length > 8" class="show-more-section">
          <button
            @click="showMoreResults"
            class="show-more-button"
          >
            Bekijk alle {{ filteredResults.length }} resultaten
          </button>
        </div>
      </div>
    </div>

    <!-- World Map Modal -->
    <div
      v-if="showMapModal"
      class="modal-overlay"
      @click="closeMapModal"
    >
      <div
        class="modal-content map-modal"
        @click.stop
      >
        <!-- Modal Header -->
        <div class="modal-header">
          <div class="modal-title-section">
            <h3 class="modal-title">Wereldkaart - Bestemmingen</h3>
            <p class="modal-subtitle">{{ availableCountries.length }} landen met vakanties</p>
          </div>
          <button
            @click="closeMapModal"
            class="modal-close-button"
          >
            <svg class="close-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Map Container -->
        <div class="map-container">
          <div id="destination-map" class="map-element"></div>
          
          <!-- Map Loading -->
          <div v-if="!mapLoaded" class="map-loading">
            <div class="loading-content">
              <div class="large-spinner"></div>
              <p class="loading-text">Kaart laden...</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- All Destinations List Modal -->
    <div
      v-if="showListModal"
      class="modal-overlay"
      @click="closeListModal"
    >
      <div
        class="modal-content list-modal"
        @click.stop
      >
        <!-- Modal Header -->
        <div class="modal-header">
          <div class="modal-header-content">
            <!-- Back Button -->
            <button
              v-if="modalView === 'country-vacations'"
              @click="goBackToCountries"
              class="back-button"
              title="Terug naar landen"
            >
              <svg class="back-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            <div class="modal-title-section">
              <h3 class="modal-title">
                {{ modalView === 'countries' ? 'Alle Bestemmingen' : selectedModalCountry?.name + ' Vakanties' }}
              </h3>
              <p class="modal-subtitle">
                <template v-if="modalView === 'countries'">
                  {{ availableCountries.length }} landen beschikbaar
                </template>
                <template v-else>
                  {{ selectedCountryVacations.length }} vakanties in {{ selectedModalCountry?.name }}
                </template>
              </p>
            </div>
          </div>
          
          <button
            @click="closeListModal"
            class="modal-close-button"
          >
            <svg class="close-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Search in Modal -->
        <div class="modal-search">
          <input
            v-model="modalSearchQuery"
            type="text"
            :placeholder="modalView === 'countries' ? 'Filter bestemmingen...' : `Filter ${selectedModalCountry?.name} vakanties...`"
            class="modal-search-input"
          />
        </div>

        <!-- Modal Content -->
        <div class="modal-body">
          
          <!-- Countries View -->
          <div v-if="modalView === 'countries'">
            <!-- Matching Continents (when searching in countries view) -->
            <div v-if="modalFilteredContinents.length > 0" class="search-continents-section">
              <h4 class="section-header">
                🌍 Continenten
                <span class="section-count">({{ modalFilteredContinents.length }})</span>
              </h4>
              <div class="continents-grid">
                <button
                  v-for="continent in modalFilteredContinents"
                  :key="continent.key"
                  @click="selectContinentFromModal(continent)"
                  class="continent-card"
                  :title="`Bekijk ${continent.vacationCount} landen in ${continent.name}`"
                >
                  <span class="continent-flag">{{ continent.flag }}</span>
                  <div class="continent-info">
                    <div class="continent-name">{{ continent.name }}</div>
                    <div class="continent-description">{{ continent.description }}</div>
                    <div class="continent-country-count">{{ continent.vacationCount }} landen</div>
                  </div>
                  <svg class="continent-arrow" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </button>
              </div>
            </div>

            <!-- Countries grouped by continent -->
            <div v-for="continent in continentsWithCountries" :key="continent.name" class="continent-section">
              <h4 class="continent-header">
                <span class="continent-emoji">{{ continent.emoji }}</span>
                {{ continent.name }}
                <span class="continent-count">({{ continent.countries.length }})</span>
              </h4>
              <div class="countries-grid">
                <button
                  v-for="country in continent.countries"
                  :key="country.name"
                  @click="navigateToCountryVacations(country)"
                  class="country-card"
                  :title="`Bekijk ${country.vacationCount} vakanties in ${country.name}`"
                >
                  <span class="country-flag">{{ country.flag }}</span>
                  <div class="country-info">
                    <div class="country-name">{{ country.name }}</div>
                    <div class="country-vacation-count">{{ country.vacationCount }} vakanties</div>
                  </div>
                  <svg class="country-arrow" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
            
            <!-- Matching Vacations (when searching in countries view) -->
            <div v-if="modalFilteredVacations.length > 0" class="search-vacations-section">
              <h4 class="section-header">
                🏖️ Vakanties
                <span class="section-count">({{ modalFilteredVacations.length }})</span>
              </h4>
              <div class="vacations-grid">
                <button
                  v-for="vacation in modalFilteredVacations"
                  :key="vacation.id"
                  @click="selectVacationFromModal(vacation)"
                  class="vacation-card"
                  :title="`Klik om naar ${vacation.name} te gaan`"
                >
                  <div class="vacation-image-wrapper">
                    <img 
                      v-if="vacation.image" 
                      :src="vacation.image" 
                      :alt="vacation.name"
                      class="vacation-image"
                      @error="$event.target.style.display='none'"
                    />
                    <div v-else class="vacation-image-placeholder">
                      <svg class="vacation-placeholder-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                    </div>
                  </div>
                  <div class="vacation-content">
                    <div class="vacation-name">{{ vacation.name }}</div>
                    <div class="vacation-title">{{ vacation.title }}</div>
                    <div class="vacation-description">{{ vacation.description }}</div>
                    <div class="vacation-meta">
                      <span v-if="vacation.country" class="vacation-country">{{ vacation.country.flag }} {{ vacation.country.name }}</span>
                    </div>
                  </div>
                  <div class="vacation-arrow-wrapper">
                    <svg class="vacation-arrow" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </button>
              </div>
            </div>
          </div>
          
          <!-- Country Vacations View -->
          <div v-else-if="modalView === 'country-vacations' && selectedModalCountry">
            <div class="country-vacations-grid">
              <button
                v-for="vacation in selectedCountryVacations"
                :key="vacation.id"
                @click="selectVacationFromModal(vacation)"
                class="country-vacation-card"
                :title="`Klik om naar ${vacation.name} te gaan`"
              >
                <div class="country-vacation-image-wrapper">
                  <img 
                    v-if="vacation.image" 
                    :src="vacation.image" 
                    :alt="vacation.name"
                    class="country-vacation-image"
                    @error="$event.target.style.display='none'"
                  />
                  <div v-else class="country-vacation-image-placeholder">
                    <svg class="country-vacation-placeholder-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                  </div>
                </div>
                <div class="country-vacation-content">
                  <div class="country-vacation-name">{{ vacation.name }}</div>
                  <div class="country-vacation-title">{{ vacation.title }}</div>
                  <div class="country-vacation-description">{{ vacation.description }}</div>
                  <div class="country-vacation-meta">
                    <span class="country-vacation-badge">{{ selectedModalCountry.flag }} {{ selectedModalCountry.name }}</span>
                  </div>
                </div>
                <div class="country-vacation-arrow-wrapper">
                  <svg class="country-vacation-arrow" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </div>
              </button>
            </div>
            
            <!-- Empty state -->
            <div v-if="selectedCountryVacations.length === 0" class="empty-state">
              <svg class="empty-state-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.5-1.002-6-2.709V15a3 3 0 003 3h6a3 3 0 003-3v-2.709z" />
              </svg>
              <h3 class="empty-state-title">Geen vakanties gevonden</h3>
              <p class="empty-state-text">Er zijn momenteel geen vakanties beschikbaar voor {{ selectedModalCountry.name }}.</p>
            </div>
          </div>
          
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import UTDService from "@/services/UTDService";

export default {
  name: 'DestinationSelector',
  data() {
    return {
      searchQuery: '',
      modalSearchQuery: '',
      showSuggestions: false,
      showMapModal: false,
      showListModal: false,
      
      // Modal navigation state
      modalView: 'countries', // 'countries' or 'country-vacations'
      selectedModalCountry: null,
      loading: false,
      error: false,
      
      // Vacation data
      vacations: [],
      
      // Countries data
      countries: [],
      
      // Caching settings (1 hour = 3600000ms)
      cacheKey: 'selectair_vacations_cache',
      countriesCacheKey: 'selectair_countries_cache',
      cacheExpiry: 3600000, // 1 hour in milliseconds
      
      // Map state
      map: null,
      mapLoaded: false,
      markers: [],
      
      // Country mappings with flags and coordinates
      countryMappings: {
        // Europe
        'nederland': { name: 'Nederland', flag: '🇳🇱', continent: 'Europa', lat: 52.3676, lng: 4.9041 },
        'belgië': { name: 'België', flag: '🇧🇪', continent: 'Europa', lat: 50.8503, lng: 4.3517 },
        'belgie': { name: 'België', flag: '🇧🇪', continent: 'Europa', lat: 50.8503, lng: 4.3517 },
        'duitsland': { name: 'Duitsland', flag: '🇩🇪', continent: 'Europa', lat: 51.1657, lng: 10.4515 },
        'frankrijk': { name: 'Frankrijk', flag: '🇫🇷', continent: 'Europa', lat: 46.2276, lng: 2.2137 },
        'spanje': { name: 'Spanje', flag: '🇪🇸', continent: 'Europa', lat: 40.4637, lng: -3.7492 },
        'italië': { name: 'Italië', flag: '🇮🇹', continent: 'Europa', lat: 41.8719, lng: 12.5674 },
        'italie': { name: 'Italië', flag: '🇮🇹', continent: 'Europa', lat: 41.8719, lng: 12.5674 },
        'griekenland': { name: 'Griekenland', flag: '🇬🇷', continent: 'Europa', lat: 39.0742, lng: 21.8243 },
        'portugal': { name: 'Portugal', flag: '🇵🇹', continent: 'Europa', lat: 39.3999, lng: -8.2245 },
        'oostenrijk': { name: 'Oostenrijk', flag: '🇦🇹', continent: 'Europa', lat: 47.5162, lng: 14.5501 },
        'zwitserland': { name: 'Zwitserland', flag: '🇨🇭', continent: 'Europa', lat: 46.8182, lng: 8.2275 },
        'verenigd koninkrijk': { name: 'Verenigd Koninkrijk', flag: '🇬🇧', continent: 'Europa', lat: 55.3781, lng: -3.4360 },
        'engeland': { name: 'Verenigd Koninkrijk', flag: '🇬🇧', continent: 'Europa', lat: 55.3781, lng: -3.4360 },
        'noorwegen': { name: 'Noorwegen', flag: '🇳🇴', continent: 'Europa', lat: 60.4720, lng: 8.4689 },
        'zweden': { name: 'Zweden', flag: '🇸🇪', continent: 'Europa', lat: 60.1282, lng: 18.6435 },
        'denemarken': { name: 'Denemarken', flag: '🇩🇰', continent: 'Europa', lat: 56.2639, lng: 9.5018 },
        'finland': { name: 'Finland', flag: '🇫🇮', continent: 'Europa', lat: 61.9241, lng: 25.7482 },
        'polen': { name: 'Polen', flag: '🇵🇱', continent: 'Europa', lat: 51.9194, lng: 19.1451 },
        'tsjechië': { name: 'Tsjechië', flag: '🇨🇿', continent: 'Europa', lat: 49.8175, lng: 15.4730 },
        'tsjechie': { name: 'Tsjechië', flag: '🇨🇿', continent: 'Europa', lat: 49.8175, lng: 15.4730 },
        'kroatië': { name: 'Kroatië', flag: '🇭🇷', continent: 'Europa', lat: 45.1000, lng: 15.2000 },
        'kroatie': { name: 'Kroatië', flag: '🇭🇷', continent: 'Europa', lat: 45.1000, lng: 15.2000 },
        'turkije': { name: 'Turkije', flag: '🇹🇷', continent: 'Europa', lat: 38.9637, lng: 35.2433 },
        'bahamas': { name: 'Bahamas', flag: '🇧🇸', continent: 'Europa', lat: 25.0343, lng: -77.3963 },
        
        // Asia
        'japan': { name: 'Japan', flag: '🇯🇵', continent: 'Azië', lat: 36.2048, lng: 138.2529 },
        'china': { name: 'China', flag: '🇨🇳', continent: 'Azië', lat: 35.8617, lng: 104.1954 },
        'thailand': { name: 'Thailand', flag: '🇹🇭', continent: 'Azië', lat: 15.8700, lng: 100.9925 },
        'vietnam': { name: 'Vietnam', flag: '🇻🇳', continent: 'Azië', lat: 14.0583, lng: 108.2772 },
        'indonesië': { name: 'Indonesië', flag: '🇮🇩', continent: 'Azië', lat: -0.7893, lng: 113.9213 },
        'indonesie': { name: 'Indonesië', flag: '🇮🇩', continent: 'Azië', lat: -0.7893, lng: 113.9213 },
        'maleisië': { name: 'Maleisië', flag: '🇲🇾', continent: 'Azië', lat: 4.2105, lng: 101.9758 },
        'maleisie': { name: 'Maleisië', flag: '🇲🇾', continent: 'Azië', lat: 4.2105, lng: 101.9758 },
        'singapore': { name: 'Singapore', flag: '🇸🇬', continent: 'Azië', lat: 1.3521, lng: 103.8198 },
        'zuid-korea': { name: 'Zuid-Korea', flag: '🇰🇷', continent: 'Azië', lat: 35.9078, lng: 127.7669 },
        'india': { name: 'India', flag: '🇮🇳', continent: 'Azië', lat: 20.5937, lng: 78.9629 },
        'nepal': { name: 'Nepal', flag: '🇳🇵', continent: 'Azië', lat: 28.3949, lng: 84.1240 },
        'sri lanka': { name: 'Sri Lanka', flag: '🇱🇰', continent: 'Azië', lat: 7.8731, lng: 80.7718 },
        'cambodja': { name: 'Cambodja', flag: '🇰🇭', continent: 'Azië', lat: 12.5657, lng: 104.9910 },
        'myanmar': { name: 'Myanmar', flag: '🇲🇲', continent: 'Azië', lat: 21.9162, lng: 95.9560 },
        
        // Africa
        'zuid-afrika': { name: 'Zuid-Afrika', flag: '🇿🇦', continent: 'Afrika', lat: -30.5595, lng: 22.9375 },
        'kenia': { name: 'Kenia', flag: '🇰🇪', continent: 'Afrika', lat: -0.0236, lng: 37.9062 },
        'tanzania': { name: 'Tanzania', flag: '🇹🇿', continent: 'Afrika', lat: -6.3690, lng: 34.8888 },
        'marokko': { name: 'Marokko', flag: '🇲🇦', continent: 'Afrika', lat: 31.7917, lng: -7.0926 },
        'egypte': { name: 'Egypte', flag: '🇪🇬', continent: 'Afrika', lat: 26.0975, lng: 31.1096 },
        'tunesië': { name: 'Tunesië', flag: '🇹🇳', continent: 'Afrika', lat: 33.8869, lng: 9.5375 },
        'tunesie': { name: 'Tunesië', flag: '🇹🇳', continent: 'Afrika', lat: 33.8869, lng: 9.5375 },
        
        // North America
        'verenigde staten': { name: 'Verenigde Staten', flag: '🇺🇸', continent: 'Noord-Amerika', lat: 37.0902, lng: -95.7129 },
        'amerika': { name: 'Verenigde Staten', flag: '🇺🇸', continent: 'Noord-Amerika', lat: 37.0902, lng: -95.7129 },
        'usa': { name: 'Verenigde Staten', flag: '🇺🇸', continent: 'Noord-Amerika', lat: 37.0902, lng: -95.7129 },
        'canada': { name: 'Canada', flag: '🇨🇦', continent: 'Noord-Amerika', lat: 56.1304, lng: -106.3468 },
        'mexico': { name: 'Mexico', flag: '🇲🇽', continent: 'Noord-Amerika', lat: 23.6345, lng: -102.5528 },
        'costa rica': { name: 'Costa Rica', flag: '🇨🇷', continent: 'Noord-Amerika', lat: 9.7489, lng: -83.7534 },
        'cuba': { name: 'Cuba', flag: '🇨🇺', continent: 'Noord-Amerika', lat: 21.5218, lng: -77.7812 },
        'aruba': { name: 'Aruba', flag: '🇦🇼', continent: 'Noord-Amerika', lat: 12.5211, lng: -69.9683 },
        
        // South America
        'brazilië': { name: 'Brazilië', flag: '🇧🇷', continent: 'Zuid-Amerika', lat: -14.2350, lng: -51.9253 },
        'brazilie': { name: 'Brazilië', flag: '🇧🇷', continent: 'Zuid-Amerika', lat: -14.2350, lng: -51.9253 },
        'argentinië': { name: 'Argentinië', flag: '🇦🇷', continent: 'Zuid-Amerika', lat: -38.4161, lng: -63.6167 },
        'argentinie': { name: 'Argentinië', flag: '🇦🇷', continent: 'Zuid-Amerika', lat: -38.4161, lng: -63.6167 },
        'peru': { name: 'Peru', flag: '🇵🇪', continent: 'Zuid-Amerika', lat: -9.1900, lng: -75.0152 },
        'chili': { name: 'Chili', flag: '🇨🇱', continent: 'Zuid-Amerika', lat: -35.6751, lng: -71.5430 },
        'colombia': { name: 'Colombia', flag: '🇨🇴', continent: 'Zuid-Amerika', lat: 4.5709, lng: -74.2973 },
        'ecuador': { name: 'Ecuador', flag: '🇪🇨', continent: 'Zuid-Amerika', lat: -1.8312, lng: -78.1834 },
        
        // Oceania
        'australië': { name: 'Australië', flag: '🇦🇺', continent: 'Oceanië', lat: -25.2744, lng: 133.7751 },
        'australie': { name: 'Australië', flag: '🇦🇺', continent: 'Oceanië', lat: -25.2744, lng: 133.7751 },
        'nieuw-zeeland': { name: 'Nieuw-Zeeland', flag: '🇳🇿', continent: 'Oceanië', lat: -40.9006, lng: 174.8860 },
        'fiji': { name: 'Fiji', flag: '🇫🇯', continent: 'Oceanië', lat: -16.5781, lng: 179.4144 },
        
        // Additional mappings
        'dubai': { name: 'Verenigde Arabische Emiraten', flag: '🇦🇪', continent: 'Azië', lat: 25.276987, lng: 55.296249 },
        'verenigde arabische emiraten': { name: 'Verenigde Arabische Emiraten', flag: '🇦🇪', continent: 'Azië', lat: 25.276987, lng: 55.296249 },
        
        // Recently added countries
        'namibië': { name: 'Namibië', flag: '🇳🇦', continent: 'Afrika', lat: -22.9576, lng: 18.4904 },
        'namibie': { name: 'Namibië', flag: '🇳🇦', continent: 'Afrika', lat: -22.9576, lng: 18.4904 },
        'canarische eilanden': { name: 'Canarische Eilanden', flag: '🇮🇨', continent: 'Europa', lat: 28.1248, lng: -15.4300 },
        'canarie': { name: 'Canarische Eilanden', flag: '🇮🇨', continent: 'Europa', lat: 28.1248, lng: -15.4300 },
        'groot-brittannië': { name: 'Groot-Brittannië', flag: '🇬🇧', continent: 'Europa', lat: 55.3781, lng: -3.4360 },
        'groot-brittannie': { name: 'Groot-Brittannië', flag: '🇬🇧', continent: 'Europa', lat: 55.3781, lng: -3.4360 },
        'dominicaanse republiek': { name: 'Dominicaanse Republiek', flag: '🇩🇴', continent: 'Noord-Amerika', lat: 18.7357, lng: -70.1627 },
        'malta': { name: 'Malta', flag: '🇲🇹', continent: 'Europa', lat: 35.9374, lng: 14.3754 },
        'ijsland': { name: 'IJsland', flag: '🇮🇸', continent: 'Europa', lat: 64.9631, lng: -19.0208 },
        
        // Countries from console log - Asia
        'jordanië': { name: 'Jordanië', flag: '🇯🇴', continent: 'Azië', lat: 30.5852, lng: 36.2384 },
        'jordanie': { name: 'Jordanië', flag: '🇯🇴', continent: 'Azië', lat: 30.5852, lng: 36.2384 },
        'turkmenistan': { name: 'Turkmenistan', flag: '🇹🇲', continent: 'Azië', lat: 38.9697, lng: 59.5563 },
        'seychellen': { name: 'Seychellen', flag: '🇸🇨', continent: 'Azië', lat: -4.6796, lng: 55.4920 },
        'oman': { name: 'Oman', flag: '🇴🇲', continent: 'Azië', lat: 21.4735, lng: 55.9754 },
        'oezbekistan': { name: 'Oezbekistan', flag: '🇺🇿', continent: 'Azië', lat: 41.3775, lng: 64.5853 },
        'malediven': { name: 'Malediven', flag: '🇲🇻', continent: 'Azië', lat: 3.2028, lng: 73.2207 },
        
        // Countries from console log - Europe
        'montenegro': { name: 'Montenegro', flag: '🇲🇪', continent: 'Europa', lat: 42.7087, lng: 19.3744 },
        'litouwen': { name: 'Litouwen', flag: '🇱🇹', continent: 'Europa', lat: 55.1694, lng: 23.8813 },
        'lapland': { name: 'Lapland', flag: '🇫🇮', continent: 'Europa', lat: 68.9792, lng: 27.0188 },
        'ierland': { name: 'Ierland', flag: '🇮🇪', continent: 'Europa', lat: 53.1424, lng: -7.6921 },
        'estland': { name: 'Estland', flag: '🇪🇪', continent: 'Europa', lat: 58.5953, lng: 25.0136 },
        'cyprus': { name: 'Cyprus', flag: '🇨🇾', continent: 'Europa', lat: 35.1264, lng: 33.4299 },
        
        // Countries from console log - North America
        'jamaica': { name: 'Jamaica', flag: '🇯🇲', continent: 'Noord-Amerika', lat: 18.1096, lng: -77.2975 },
        'honduras': { name: 'Honduras', flag: '🇭🇳', continent: 'Noord-Amerika', lat: 15.2000, lng: -86.2419 },
        'curaçao': { name: 'Curaçao', flag: '🇨🇼', continent: 'Noord-Amerika', lat: 12.1696, lng: -68.9900 },
        'curacao': { name: 'Curaçao', flag: '🇨🇼', continent: 'Noord-Amerika', lat: 12.1696, lng: -68.9900 },
        'bahama\'s': { name: 'Bahama\'s', flag: '🇧🇸', continent: 'Noord-Amerika', lat: 25.0343, lng: -77.3963 },
        'alaska': { name: 'Alaska', flag: '🇺🇸', continent: 'Noord-Amerika', lat: 61.2181, lng: -149.9003 },
        
        // Countries from console log - Africa
        'mauritius': { name: 'Mauritius', flag: '🇲🇺', continent: 'Afrika', lat: -20.3484, lng: 57.5522 },
        'zimbabwe': { name: 'Zimbabwe', flag: '🇿🇼', continent: 'Afrika', lat: -19.0154, lng: 29.1549 },
        'zanzibar': { name: 'Zanzibar', flag: '🇹🇿', continent: 'Afrika', lat: -6.1659, lng: 39.2026 },
        'oeganda': { name: 'Oeganda', flag: '🇺🇬', continent: 'Afrika', lat: 1.3733, lng: 32.2903 },
        'uganda': { name: 'Oeganda', flag: '🇺🇬', continent: 'Afrika', lat: 1.3733, lng: 32.2903 },
        'senegal': { name: 'Senegal', flag: '🇸🇳', continent: 'Afrika', lat: 14.4974, lng: -14.4524 },
        'madagaskar': { name: 'Madagaskar', flag: '🇲🇬', continent: 'Afrika', lat: -18.7669, lng: 46.8691 },
        'madagascar': { name: 'Madagaskar', flag: '🇲🇬', continent: 'Afrika', lat: -18.7669, lng: 46.8691 },
        'kaapverdië': { name: 'Kaapverdië', flag: '🇨🇻', continent: 'Afrika', lat: 16.5388, lng: -24.0132 },
        'kaapverdie': { name: 'Kaapverdië', flag: '🇨🇻', continent: 'Afrika', lat: 16.5388, lng: -24.0132 },
        'gabon': { name: 'Gabon', flag: '🇬🇦', continent: 'Afrika', lat: -0.8037, lng: 11.6094 },
        'botswana': { name: 'Botswana', flag: '🇧🇼', continent: 'Afrika', lat: -22.3285, lng: 24.6849 },

        // New Afrika countries
        'ethiopië': { name: 'Ethiopië', flag: '🇪🇹', continent: 'Afrika', lat: 9.1450, lng: 40.4897 },
        'ethiopie': { name: 'Ethiopië', flag: '🇪🇹', continent: 'Afrika', lat: 9.1450, lng: 40.4897 },
        'gambia': { name: 'Gambia', flag: '🇬🇲', continent: 'Afrika', lat: 13.4432, lng: -15.3101 },
        'malawi': { name: 'Malawi', flag: '🇲🇼', continent: 'Afrika', lat: -13.2543, lng: 34.3015 },
        'rwanda': { name: 'Rwanda', flag: '🇷🇼', continent: 'Afrika', lat: -1.9403, lng: 29.8739 },
        'zambia': { name: 'Zambia', flag: '🇿🇲', continent: 'Afrika', lat: -13.1339, lng: 27.8493 },

        // New Europa countries
        'albanië': { name: 'Albanië', flag: '🇦🇱', continent: 'Europa', lat: 41.1533, lng: 20.1683 },
        'albanie': { name: 'Albanië', flag: '🇦🇱', continent: 'Europa', lat: 41.1533, lng: 20.1683 },
        'bulgarije': { name: 'Bulgarije', flag: '🇧🇬', continent: 'Europa', lat: 42.7339, lng: 25.4858 },
        'hongarije': { name: 'Hongarije', flag: '🇭🇺', continent: 'Europa', lat: 47.1625, lng: 19.5033 },
        'kosovo': { name: 'Kosovo', flag: '🇽🇰', continent: 'Europa', lat: 42.6026, lng: 20.9030 },
        'letland': { name: 'Letland', flag: '🇱🇻', continent: 'Europa', lat: 56.8796, lng: 24.6032 },
        'noord-macedonië': { name: 'Noord-Macedonië', flag: '🇲🇰', continent: 'Europa', lat: 41.5124, lng: 21.7453 },
        'noord-macedonie': { name: 'Noord-Macedonië', flag: '🇲🇰', continent: 'Europa', lat: 41.5124, lng: 21.7453 },
        'roemenië': { name: 'Roemenië', flag: '🇷🇴', continent: 'Europa', lat: 45.9432, lng: 24.9668 },
        'roemenie': { name: 'Roemenië', flag: '🇷🇴', continent: 'Europa', lat: 45.9432, lng: 24.9668 },
        'slovenië': { name: 'Slovenië', flag: '🇸🇮', continent: 'Europa', lat: 46.1512, lng: 14.9955 },
        'slovenie': { name: 'Slovenië', flag: '🇸🇮', continent: 'Europa', lat: 46.1512, lng: 14.9955 },

        // New Midden-Amerika countries
        'belize': { name: 'Belize', flag: '🇧🇿', continent: 'Midden-Amerika', lat: 17.1899, lng: -88.4976 },
        'guatemala': { name: 'Guatemala', flag: '🇬🇹', continent: 'Midden-Amerika', lat: 15.7835, lng: -90.2308 },
        'nicaragua': { name: 'Nicaragua', flag: '🇳🇮', continent: 'Midden-Amerika', lat: 12.8654, lng: -85.2072 },
        'panama': { name: 'Panama', flag: '🇵🇦', continent: 'Midden-Amerika', lat: 8.5380, lng: -80.7821 },
        'suriname': { name: 'Suriname', flag: '🇸🇷', continent: 'Midden-Amerika', lat: 3.9193, lng: -56.0278 },

        // New Zuid-Amerika countries
        'bolivië': { name: 'Bolivië', flag: '🇧🇴', continent: 'Zuid-Amerika', lat: -16.2902, lng: -63.5887 },
        'bolivie': { name: 'Bolivië', flag: '🇧🇴', continent: 'Zuid-Amerika', lat: -16.2902, lng: -63.5887 },
      },
      
      // Category mappings like in FilterSidebar
      excludeFromCountries: [
        'groepsreis', 'segments', 'citytrip', 'sport', 'countries', 'vakanties', 
        'verras mij', 'events', 'brochures', 'nieuw', 'verblijfsvakantie', 'all inclusive', 
        'weekendje weg', 'vakantiehuis', 'promoties', 'skivakantie', 'nordic reizen', 
        'vakantie met hond', 'singlereizen', 'lapland', 'rondreis', 'cruise', 'autovakantie', 
        'safari', 'vakantie aan zee', 'vakantie met kinderen', 'vliegvakanties',
        'januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus',
        'september', 'oktober', 'november', 'december', 'europa', 'afrika', 'azië',
        'noord-amerika', 'midden-amerika', 'zuid-amerika', 'oceanië', 'caraiben', 'midden-oosten'
      ],
      
      // Continent data with URLs
      continentData: [
        {
          name: 'Europa',
          key: 'europa',
          emoji: '🇪🇺',
          url: '/bestemmingen/europa/',
          description: 'Ontdek Europa\'s rijke geschiedenis en cultuur'
        },
        {
          name: 'Afrika',
          key: 'afrika', 
          emoji: '🌍',
          url: '/bestemmingen/afrika/',
          description: 'Avontuurlijke safari\'s en exotische bestemmingen'
        },
        {
          name: 'Azië',
          key: 'azie',
          emoji: '🌏',
          url: '/bestemmingen/azie/',
          description: 'Fascinerende culturen en adembenemende landschappen'
        },
        {
          name: 'Noord-Amerika',
          key: 'noord-amerika',
          emoji: '🌎',
          url: '/bestemmingen/noord-amerika/',
          description: 'Van grootsteden tot prachtige nationale parken'
        },
        {
          name: 'Midden-Amerika',
          key: 'midden-amerika',
          emoji: '🌎',
          url: '/bestemmingen/midden-amerika/',
          description: 'Tropische paradijzen en Maya-cultuur'
        },
        {
          name: 'Zuid-Amerika',
          key: 'zuid-amerika',
          emoji: '🌎',
          url: '/bestemmingen/zuid-amerika/',
          description: 'Kleurrijke culturen en spectaculaire natuur'
        },
        {
          name: 'Oceanië',
          key: 'oceanie',
          emoji: '🌏',
          url: '/bestemmingen/oceanie/',
          description: 'Paradijselijke eilanden en unieke wildlife'
        }
      ]
    }
  },
  
  computed: {
    shopSettings() {
      return this.$root.shopSettings || {}
    },
    
    // Use actual countries from API
    availableCountries() {
      return this.countries.map(country => {
        // Get continent from categories (e.g., "in Afrika" -> "Afrika")
        const continentCategory = country.metadata?.categories?.find(cat => cat.startsWith('in '))
        const continent = continentCategory ? continentCategory.replace('in ', '') : 'Overig'
        
        // Get country mapping for flag and coordinates
        const countryKey = country.name.toLowerCase()
        const countryMapping = this.countryMappings[countryKey] || {}
        
        // Get vacation count for this country from vacation data
        const vacationCount = this.getCountryVacationCount(countryKey)
        
        return {
          id: country.id,
          key: countryKey,
          name: country.name,
          flag: countryMapping.flag || '🏳️',
          continent: this.mapContinentName(continent),
          lat: countryMapping.lat || 0,
          lng: countryMapping.lng || 0,
          vacationCount,
          url: country.bloggerPageUrl,
          image: country.metadata?.image || country.images?.[0] || '',
          description: country.metadata?.description || ''
        }
      }).filter(country => country.vacationCount > 0)
        .sort((a, b) => b.vacationCount - a.vacationCount)
    },
    
    filteredResults() {
      if (!this.searchQuery || this.searchQuery.length < 1) return []
      
      const query = this.searchQuery.toLowerCase()
      const results = []
      
      // Add matching continents
      const matchingContinents = this.continentData.filter(continent => 
        continent.name.toLowerCase().includes(query) ||
        continent.key.toLowerCase().includes(query)
      ).map(continent => ({
        type: 'continent',
        ...continent,
        flag: continent.emoji,
        vacationCount: this.getCountriesInContinent(continent.name).length
      }))
      
      // Add matching countries
      const matchingCountries = this.availableCountries.filter(country => 
        country.name.toLowerCase().includes(query) ||
        country.continent.toLowerCase().includes(query)
      ).map(country => ({
        type: 'country',
        ...country
      }))
      
      // Add matching vacations
      const matchingVacations = this.vacations.filter(vacation => {
        const name = vacation.name?.toLowerCase() || ''
        const title = vacation.metadata?.title?.toLowerCase() || ''
        const description = vacation.metadata?.description?.toLowerCase() || ''
        
        return name.includes(query) || 
               title.includes(query) || 
               description.includes(query)
      }).map(vacation => ({
        type: 'vacation',
        id: vacation.id,
        name: vacation.name,
        title: vacation.metadata?.title || vacation.name,
        description: vacation.metadata?.description || '',
        image: vacation.metadata?.image || vacation.images?.[0] || '',
        categories: vacation.metadata?.categories || [],
        url: vacation.bloggerPageUrl || '',
        // Get country info from categories if available
        country: this.getVacationCountry(vacation)
      }))
      
      // Combine and sort: continents first, then countries, then vacations
      results.push(...matchingContinents.slice(0, 2)) // Max 2 continents
      results.push(...matchingCountries.slice(0, 3)) // Max 3 countries
      results.push(...matchingVacations.slice(0, 8)) // Max 8 vacations
      
      return results.slice(0, 10) // Max 10 total results
    },
    
    // For backward compatibility
    filteredCountries() {
      return this.filteredResults.filter(result => result.type === 'country')
    },
    
    continentsWithCountries() {
      const continentMap = {}
      
      // Group countries by continent
      this.availableCountries.filter(country => {
        const query = this.modalSearchQuery.toLowerCase()
        return !query || 
               country.name.toLowerCase().includes(query) ||
               country.continent.toLowerCase().includes(query)
      }).forEach(country => {
        if (!continentMap[country.continent]) {
          continentMap[country.continent] = {
            name: country.continent,
            emoji: this.getContinentEmoji(country.continent),
            countries: []
          }
        }
        continentMap[country.continent].countries.push(country)
      })
      
      // Convert to array and sort by continent order
      const continentOrder = ['Europa', 'Azië', 'Afrika', 'Noord-Amerika', 'Midden-Amerika', 'Zuid-Amerika', 'Oceanië']
      return continentOrder
        .filter(continentName => continentMap[continentName])
        .map(continentName => continentMap[continentName])
    },
    
    modalFilteredVacations() {
      if (!this.modalSearchQuery || this.modalSearchQuery.length < 1) return []
      
      const query = this.modalSearchQuery.toLowerCase()
      return this.vacations.filter(vacation => {
        const name = vacation.name?.toLowerCase() || ''
        const title = vacation.metadata?.title?.toLowerCase() || ''
        const description = vacation.metadata?.description?.toLowerCase() || ''
        
        return name.includes(query) || 
               title.includes(query) || 
               description.includes(query)
      }).map(vacation => ({
        type: 'vacation',
        id: vacation.id,
        name: vacation.name,
        title: vacation.metadata?.title || vacation.name,
        description: vacation.metadata?.description || '',
        image: vacation.metadata?.image || vacation.images?.[0] || '',
        categories: vacation.metadata?.categories || [],
        url: vacation.bloggerPageUrl || '',
        country: this.getVacationCountry(vacation)
      })).slice(0, 20) // Limit to 20 vacations
    },
    
    modalFilteredContinents() {
      if (!this.modalSearchQuery || this.modalSearchQuery.length < 1) return []
      
      const query = this.modalSearchQuery.toLowerCase()
      return this.continentData.filter(continent => 
        continent.name.toLowerCase().includes(query) ||
        continent.key.toLowerCase().includes(query) ||
        continent.description.toLowerCase().includes(query)
      ).map(continent => ({
        type: 'continent',
        ...continent,
        flag: continent.emoji,
        vacationCount: this.getCountriesInContinent(continent.name).length
      }))
    },
    
    selectedCountryVacations() {
      if (!this.selectedModalCountry) return []
      
      const countryVacations = this.getCountryVacations(this.selectedModalCountry.key)
      
      // Apply search filter if there's a search query
      if (this.modalSearchQuery && this.modalSearchQuery.length > 0) {
        const query = this.modalSearchQuery.toLowerCase()
        return countryVacations.filter(vacation => {
          const name = vacation.name?.toLowerCase() || ''
          const title = vacation.title?.toLowerCase() || ''
          const description = vacation.description?.toLowerCase() || ''
          
          return name.includes(query) || 
                 title.includes(query) || 
                 description.includes(query)
        })
      }
      
      return countryVacations
    }
  },
  
  methods: {
    async loadVacations() {
      this.loading = true
      this.error = false

      try {
        // Check cache first
        const cached = this.getCachedVacations()
        if (cached) {
          this.vacations = cached
          this.loading = false
          return
        }

        // Fetch from API if no cache or expired
        const vacations = await UTDService.getProducts(
          this.shopSettings.label, 
          this.shopSettings.lang, 
          this.shopSettings.siteId
        )
        
        this.vacations = vacations
        this.setCachedVacations(vacations)
      } catch (error) {
        console.error("Error loading vacations:", error)
        this.error = true
        
        // Try to load from cache even if expired on error
        const cached = this.getCachedVacations(true)
        if (cached) {
          this.vacations = cached
          this.error = false
          console.log("Loaded from expired cache due to API error")
        }
      } finally {
        this.loading = false
      }
    },
    
    async loadCountries() {
      try {
        // Check cache first
        const cached = this.getCachedCountries()
        if (cached) {
          this.countries = cached
          return
        }

        // Fetch from API if no cache or expired
        const siteId = this.shopSettings.siteId
        const response = await fetch(
          `https://www.uptodateconnect.com/api/v1/site-builder/meta-details?siteId=${siteId}&label=country&limit=999&offset=0&order=priority`
        )
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        
        const data = await response.json()
        
        if (data.success && data.payload && data.payload.results) {
          this.countries = data.payload.results
          this.setCachedCountries(data.payload.results)
          console.log(`Loaded ${data.payload.results.length} countries from API`)
        } else {
          throw new Error('Invalid API response format')
        }
      } catch (error) {
        console.error("Error loading countries:", error)
        
        // Try to load from cache even if expired on error
        const cached = this.getCachedCountries(true)
        if (cached) {
          this.countries = cached
          console.log("Loaded countries from expired cache due to API error")
        }
      }
    },
    
    getCachedVacations(ignoreExpiry = false) {
      try {
        const cached = localStorage.getItem(this.cacheKey)
        if (!cached) return null
        
        const data = JSON.parse(cached)
        const now = Date.now()
        
        // Check if cache is expired (unless ignoring expiry)
        if (!ignoreExpiry && (now - data.timestamp) > this.cacheExpiry) {
          localStorage.removeItem(this.cacheKey)
          return null
        }
        
        console.log(`Loaded ${data.vacations.length} vacations from cache`)
        return data.vacations
      } catch (error) {
        console.error("Error reading cache:", error)
        localStorage.removeItem(this.cacheKey)
        return null
      }
    },
    
    setCachedVacations(vacations) {
      try {
        const data = {
          vacations: vacations,
          timestamp: Date.now()
        }
        localStorage.setItem(this.cacheKey, JSON.stringify(data))
        console.log(`Cached ${vacations.length} vacations for 1 hour`)
      } catch (error) {
        console.error("Error setting cache:", error)
      }
    },
    
    getCachedCountries(ignoreExpiry = false) {
      try {
        const cached = localStorage.getItem(this.countriesCacheKey)
        if (!cached) return null
        
        const data = JSON.parse(cached)
        const now = Date.now()
        
        // Check if cache is expired (unless ignoring expiry)
        if (!ignoreExpiry && (now - data.timestamp) > this.cacheExpiry) {
          localStorage.removeItem(this.countriesCacheKey)
          return null
        }
        
        console.log(`Loaded ${data.countries.length} countries from cache`)
        return data.countries
      } catch (error) {
        console.error("Error reading countries cache:", error)
        localStorage.removeItem(this.countriesCacheKey)
        return null
      }
    },
    
    setCachedCountries(countries) {
      try {
        const data = {
          countries: countries,
          timestamp: Date.now()
        }
        localStorage.setItem(this.countriesCacheKey, JSON.stringify(data))
        console.log(`Cached ${countries.length} countries for 1 hour`)
      } catch (error) {
        console.error("Error setting countries cache:", error)
      }
    },
    
    mapContinentName(continent) {
      const continentMap = {
        'Afrika': 'Afrika',
        'Azië': 'Azië',
        'Europa': 'Europa',
        'Noord-Amerika': 'Noord-Amerika',
        'Midden-Amerika': 'Midden-Amerika',
        'Zuid-Amerika': 'Zuid-Amerika',
        'Oceanië': 'Oceanië'
      }
      return continentMap[continent] || continent
    },
    
    getCountryVacationCount(countryKey) {
      return this.vacations.filter(vacation => 
        vacation.metadata?.categories?.some(category => 
          category.toLowerCase() === countryKey
        )
      ).length
    },
    
    getVacationCountry(vacation) {
      // Find the first country category in the vacation
      if (!vacation.metadata?.categories) return null
      
      for (const category of vacation.metadata.categories) {
        const lowerCategory = category.toLowerCase()
        if (this.countryMappings[lowerCategory]) {
          return {
            key: lowerCategory,
            ...this.countryMappings[lowerCategory]
          }
        }
      }
      return null
    },
    
    getCountryVacations(countryKey) {
      // Get all vacations for a specific country
      return this.vacations.filter(vacation => 
        vacation.metadata?.categories?.some(category => 
          category.toLowerCase() === countryKey
        )
      ).map(vacation => ({
        id: vacation.id,
        name: vacation.name,
        title: vacation.metadata?.title || vacation.name,
        description: vacation.metadata?.description || '',
        image: vacation.metadata?.image || vacation.images?.[0] || '',
        url: vacation.bloggerPageUrl || '',
        pageId: vacation.pageId,
        categories: vacation.metadata?.categories || []
      }))
    },
    
    getContinentEmoji(continent) {
      const emojiMap = {
        'Europa': '🇪🇺',
        'Azië': '🌏',
        'Afrika': '🌍',
        'Noord-Amerika': '🌎',
        'Midden-Amerika': '🌎',
        'Zuid-Amerika': '🌎',
        'Oceanië': '🌏'
      }
      return emojiMap[continent] || '🌍'
    },
    
    getCountriesInContinent(continentName) {
      return this.availableCountries.filter(country => country.continent === continentName)
    },
    
    handleSearch() {
      this.showSuggestions = this.searchQuery.length >= 1
    },
    
    closeSuggestions() {
      this.showSuggestions = false
    },
    
    selectResult(result) {
      if (result.type === 'continent') {
        this.selectContinent(result)
      } else if (result.type === 'country') {
        this.selectCountry(result)
      } else if (result.type === 'vacation') {
        this.selectVacation(result)
      }
    },
    
    selectContinent(continent) {
      this.searchQuery = continent.name
      this.showSuggestions = false
      
      // Direct navigation - redirect to continent page URL
      if (continent.url) {
        console.log(`Navigating to continent page for ${continent.name}:`, continent.url)
        window.location.href = continent.url
      } else {
        console.log(`No URL found for continent: ${continent.name}`)
      }
    },
    
    selectCountry(country) {
      this.searchQuery = country.name
      this.showSuggestions = false
      
      // Direct navigation - redirect to country page URL
      if (country.url) {
        console.log(`Navigating to country page for ${country.name}:`, country.url)
        window.location.href = country.url
      } else {
        console.log(`No URL found for country: ${country.name}`)
        // Fallback: try to get first vacation URL
        const countryVacations = this.getCountryVacations(country.key)
        if (countryVacations.length > 0 && countryVacations[0].url) {
          console.log(`Fallback: Navigating to first vacation for ${country.name}:`, countryVacations[0].url)
          window.location.href = countryVacations[0].url
        }
      }
    },
    
    selectVacation(vacation) {
      this.searchQuery = vacation.name
      this.showSuggestions = false
      
      // Direct navigation - redirect to vacation URL
      if (vacation.url) {
        console.log(`Navigating to vacation: ${vacation.name} -> ${vacation.url}`)
        window.location.href = vacation.url
      } else {
        console.log(`No URL found for vacation: ${vacation.name}`)
      }
    },
    
    navigateToCountryVacations(country) {
      this.selectedModalCountry = country
      this.modalView = 'country-vacations'
      this.modalSearchQuery = '' // Clear search when navigating
      console.log(`Navigating to vacations for ${country.name}`)
    },
    
    goBackToCountries() {
      this.modalView = 'countries'
      this.selectedModalCountry = null
      this.modalSearchQuery = '' // Clear search when going back
      console.log('Navigating back to countries')
    },
    
    selectContinentFromModal(continent) {
      this.closeListModal()
      this.selectContinent(continent)
    },
    
    selectCountryFromModal(country) {
      // This method is kept for backwards compatibility if needed
      this.closeListModal()
      this.selectCountry(country)
    },
    
    selectVacationFromModal(vacation) {
      this.closeListModal()
      this.selectVacation(vacation)
    },
    
    showMoreResults() {
      this.showSuggestions = false
      this.modalSearchQuery = this.searchQuery // Preserve search query in modal
      this.showListModal = true
    },
    
    closeMapModal() {
      this.showMapModal = false
      if (this.map) {
        this.map.remove()
        this.map = null
        this.mapLoaded = false
      }
    },
    
    closeListModal() {
      this.showListModal = false
      this.modalSearchQuery = ''
      this.modalView = 'countries'
      this.selectedModalCountry = null
    },
    
    // Map functionality
    async initializeMap() {
      await this.loadLeaflet()
      
      const mapContainer = document.getElementById('destination-map')
      if (!mapContainer) return
      
      // Clear existing map
      if (this.map) {
        this.map.remove()
      }
      
      // Initialize world map
      this.map = L.map('destination-map').setView([20, 0], 2)
      
      // Add map tiles
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 18
      }).addTo(this.map)
      
      // Add markers for countries with vacations
      this.addCountryMarkers()
      
      this.mapLoaded = true
    },
    
    addCountryMarkers() {
      // Clear existing markers
      this.markers.forEach(marker => this.map.removeLayer(marker))
      this.markers = []
      
      this.availableCountries.forEach(country => {
        if (country.vacationCount > 0) {
          // Create custom marker with country flag
          const marker = L.marker([country.lat, country.lng], {
            icon: L.divIcon({
              className: 'custom-country-marker',
              html: `
                <div class="flex items-center justify-center w-8 h-8 bg-white rounded-full shadow-lg border-2 border-theme map-flag-container">
                  <span class="text-xl">${country.flag}</span>
                </div>
              `,
              iconSize: [40, 50],
              iconAnchor: [20, 25]
            })
          }).addTo(this.map)
          
          // Use country URL for direct link
          const countryUrl = country.url || '#'
          
          // Create popup
          const popupContent = `
            <div style="text-align: center;">
              <div style="font-size: 24px; margin-bottom: 8px;">${country.flag}</div>
              <h3 style="font-weight: 600; color: #111827; margin-bottom: 4px;">${country.name}</h3>
              <p style="font-size: 14px; color: #6b7280; margin-bottom: 12px;">${country.vacationCount} vakanties beschikbaar</p>
              <a href="${countryUrl}" 
                 style="display: inline-block; padding: 8px 16px; background-color: var(--theme, #e31c18); color: white; border-radius: 6px; font-size: 14px; text-decoration: none; transition: background-color 0.2s;">
                Bekijk ${country.name}
              </a>
            </div>
          `
          
          marker.bindPopup(popupContent)
          marker.countryKey = country.key
          this.markers.push(marker)
        }
      })
    },
    
    loadLeaflet() {
      return new Promise((resolve, reject) => {
        if (window.L) {
          resolve()
          return
        }
        
        // Load CSS
        const cssLink = document.createElement('link')
        cssLink.rel = 'stylesheet'
        cssLink.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css'
        cssLink.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY='
        cssLink.crossOrigin = ''
        document.head.appendChild(cssLink)
        
        // Load JavaScript
        const script = document.createElement('script')
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js'
        script.integrity = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo='
        script.crossOrigin = ''
        script.async = true
        
        script.onload = () => resolve()
        script.onerror = () => reject(new Error('Failed to load Leaflet'))
        
        document.head.appendChild(script)
      })
    }
  },
  
  watch: {
    showMapModal(show) {
      if (show) {
        this.$nextTick(() => {
          this.initializeMap()
        })
      }
    }
  },
  
  async mounted() {
    // Load both countries and vacation data on mount
    await Promise.all([
      this.loadCountries(),
      this.loadVacations()
    ])
    
    // Click outside to close suggestions
    document.addEventListener('click', (e) => {
      if (!this.$el.contains(e.target)) {
        this.showSuggestions = false
      }
    })
  },
  
  beforeDestroy() {
    if (this.map) {
      this.map.remove()
    }
  }
}
</script>

<style scoped>
/* Theme colors - inherited from :root (set via data-theme in HTML) */
.destination-search-widget {
  --gray-50: #f9fafb;
  --gray-100: #f3f4f6;
  --gray-200: #e5e7eb;
  --gray-300: #d1d5db;
  --gray-400: #9ca3af;
  --gray-500: #6b7280;
  --gray-600: #4b5563;
  --gray-700: #374151;
  --gray-800: #1f2937;
  --gray-900: #111827;
  --blue-100: #dbeafe;
  --blue-800: #1e40af;
  --green-100: #dcfce7;
  --green-800: #166534;
  position: relative;
  z-index: 1;
}

/* Input container */
.input-container {
  position: relative;
}

.input-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Search input */
.search-input-wrapper {
  position: relative;
  flex: 1;
}

.search-input {
  width: 100%;
  padding: 12px 16px 12px 40px;
  border: 1px solid var(--gray-300);
  border-radius: 8px;
  font-size: 16px;
  outline: none;
  transition: all 0.2s;
}

.search-input:focus {
  box-shadow: 0 0 0 2px var(--theme);
  border-color: transparent;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  height: 20px;
  width: 20px;
  color: var(--gray-400);
}

.loading-indicator {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
}

.loading-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-bottom: 2px solid var(--theme);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Action buttons */
.action-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  color: white;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s;
}

.map-button {
  background-color: var(--theme);
}

.map-button:hover {
  background-color: var(--theme-hover);
}

.list-button {
  background-color: var(--gray-600);
}

.list-button:hover {
  background-color: var(--gray-700);
}

.button-icon {
  height: 20px;
  width: 20px;
}

/* Suggestions dropdown */
.suggestions-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid var(--gray-200);
  border-radius: 8px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  z-index: 9998;
  max-height: 500px;
  overflow-y: auto;
  margin-top: 4px;
}

.suggestion-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  cursor: pointer;
  border-bottom: 1px solid var(--gray-100);
  transition: background-color 0.2s;
}

.suggestion-item:hover {
  background-color: var(--gray-50);
}

.suggestion-item:last-child {
  border-bottom: none;
}

.result-flag {
  font-size: 20px;
}

.result-content {
  flex: 1;
  min-width: 0;
}

.result-name {
  font-weight: 500;
  color: var(--gray-900);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.result-subtitle {
  font-size: 14px;
  color: var(--gray-500);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.result-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
}

.country-info {
  font-size: 12px;
}

.click-hint {
  font-size: 12px;
  color: var(--gray-400);
}

.result-badge {
  padding: 2px 8px;
  font-size: 12px;
  border-radius: 9999px;
}

.continent-badge {
  background-color: #fef3c7;
  color: #92400e;
}

.country-badge {
  background-color: var(--blue-100);
  color: var(--blue-800);
}

.vacation-badge {
  background-color: var(--green-100);
  color: var(--green-800);
}

.result-image-wrapper {
  flex-shrink: 0;
}

.result-image {
  width: 48px;
  height: 48px;
  object-fit: cover;
  border-radius: 8px;
}

.result-image-placeholder {
  width: 48px;
  height: 48px;
  background-color: var(--gray-200);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.placeholder-icon {
  height: 24px;
  width: 24px;
  color: var(--gray-400);
}

.result-arrow {
  height: 16px;
  width: 16px;
  color: var(--gray-400);
  flex-shrink: 0;
}

.show-more-section {
  border-top: 1px solid var(--gray-100);
}

.show-more-button {
  width: 100%;
  padding: 12px 16px;
  font-size: 14px;
  color: var(--theme);
  background: none;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s;
  font-weight: 500;
}

.show-more-button:hover {
  background-color: var(--gray-50);
}

/* Modal overlay */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  z-index: 9999;
}

/* Modal content */
.modal-content {
  background: white;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  max-width: 100%;
  max-height: 100%;
  position: relative;
}

.map-modal {
  width: 100%;
  max-width: 1152px; /* 6xl */
  height: 85vh;
}

.list-modal {
  width: 100%;
  max-width: 896px; /* 4xl */
  max-height: 90vh;
  height: 90vh;
}

@media (max-width: 480px) {
  .list-modal {
    width: 95%;
    height: 95vh;
    max-height: 95vh;
    max-width: 95vw;
  }
  
  .modal-overlay {
    padding: 8px;
  }
}

/* Modal header */
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid var(--gray-200);
  background-color: var(--gray-50);
}

.modal-header-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.back-button {
  padding: 8px;
  color: var(--gray-500);
  background: none;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s;
}

.back-button:hover {
  color: var(--gray-700);
  background-color: var(--gray-200);
}

.back-icon {
  height: 20px;
  width: 20px;
}

/* Modal title section styles are defined by child elements */

.modal-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--gray-900);
  margin: 0;
}

.modal-subtitle {
  font-size: 14px;
  color: var(--gray-600);
  margin: 0;
}

.modal-close-button {
  padding: 8px;
  color: var(--gray-500);
  background: none;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s;
}

.modal-close-button:hover {
  color: var(--gray-700);
  background-color: var(--gray-200);
}

.close-icon {
  height: 24px;
  width: 24px;
  color: var(--gray-500);
}

/* Modal search */
.modal-search {
  padding: 16px;
  border-bottom: 1px solid var(--gray-200);
  background-color: var(--gray-50);
}

.modal-search-input {
  width: 100%;
  padding: 8px 16px;
  border: 1px solid var(--gray-300);
  border-radius: 8px;
  outline: none;
  transition: all 0.2s;
}

.modal-search-input:focus {
  box-shadow: 0 0 0 2px var(--theme);
  border-color: transparent;
}

/* Modal body */
.modal-body {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 16px;
  min-height: 0;
  max-width: 100%;
}

/* Continent sections */
.continent-section {
  margin-bottom: 24px;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
}

.continent-header {
  font-size: 16px;
  font-weight: 600;
  color: var(--gray-900);
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.continent-emoji {
  font-size: 24px;
}

.continent-count {
  font-size: 14px;
  font-weight: 400;
  color: var(--gray-500);
}

/* Countries grid */
.countries-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
}

@media (min-width: 640px) {
  .countries-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1024px) {
  .countries-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

.country-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: 1px solid var(--gray-200);
  border-radius: 8px;
  background: none;
  cursor: pointer;
  text-align: left;
  transition: all 0.2s;
}

.country-card:hover {
  border-color: var(--theme);
  background-color: var(--theme-light);
}

.country-flag {
  font-size: 20px;
}

.country-info {
  flex: 1;
  min-width: 0;
}

.country-name {
  font-weight: 500;
  color: var(--gray-900);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.country-vacation-count {
  font-size: 14px;
  color: var(--gray-500);
}

.country-arrow {
  height: 16px;
  width: 16px;
  color: var(--gray-400);
  transition: color 0.2s;
}

.country-card:hover .country-arrow {
  color: var(--theme);
}

/* Continents grid */
.continents-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
}

@media (min-width: 640px) {
  .continents-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1024px) {
  .continents-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

.continent-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border: 1px solid var(--gray-200);
  border-radius: 8px;
  background: none;
  cursor: pointer;
  text-align: left;
  transition: all 0.2s;
}

.continent-card:hover {
  border-color: #92400e;
  background-color: #fef3c7;
}

.continent-flag {
  font-size: 24px;
}

.continent-info {
  flex: 1;
  min-width: 0;
}

.continent-name {
  font-weight: 600;
  color: var(--gray-900);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 4px;
}

.continent-description {
  font-size: 13px;
  color: var(--gray-600);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 4px;
}

.continent-country-count {
  font-size: 12px;
  color: var(--gray-500);
}

.continent-arrow {
  height: 16px;
  width: 16px;
  color: var(--gray-400);
  transition: color 0.2s;
  flex-shrink: 0;
}

.continent-card:hover .continent-arrow {
  color: #92400e;
}

/* Search continents section */
.search-continents-section {
  margin-bottom: 24px;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
}

/* Search vacations section */
.search-vacations-section {
  margin-bottom: 24px;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
}

.section-header {
  font-size: 16px;
  font-weight: 600;
  color: var(--gray-900);
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.section-count {
  font-size: 14px;
  font-weight: 400;
  color: var(--gray-500);
}

/* Vacations grid */
.vacations-grid {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  margin: 0;
  padding: 0;
}

@media (min-width: 640px) {
  .vacations-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 768px) {
  .vacations-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 639px) {
  .vacations-grid {
    grid-template-columns: 1fr;
  }
}

.vacation-card {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  border: 1px solid var(--gray-200);
  border-radius: 8px;
  background: none;
  cursor: pointer;
  text-align: left;
  transition: all 0.2s;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.vacation-card:hover {
  border-color: var(--theme);
  background-color: var(--theme-light);
}

.vacation-image-wrapper {
  flex-shrink: 0;
}

.vacation-image {
  width: 64px;
  height: 64px;
  object-fit: cover;
  border-radius: 8px;
}

.vacation-image-placeholder {
  width: 64px;
  height: 64px;
  background-color: var(--gray-200);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.vacation-placeholder-icon {
  height: 32px;
  width: 32px;
  color: var(--gray-400);
}

.vacation-content {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.vacation-name {
  font-weight: 500;
  color: var(--gray-900);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.vacation-title {
  font-size: 14px;
  color: var(--gray-600);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 8px;
  max-width: 100%;
}

.vacation-description {
  font-size: 12px;
  color: var(--gray-500);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.4;
  max-height: 2.8em;
}

.vacation-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}

.vacation-country {
  font-size: 12px;
}

.vacation-arrow-wrapper {
  flex-shrink: 0;
  align-self: center;
}

.vacation-arrow {
  height: 16px;
  width: 16px;
  color: var(--gray-400);
  transition: color 0.2s;
}

.vacation-card:hover .vacation-arrow {
  color: var(--theme);
}

/* Country vacations grid */
.country-vacations-grid {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  margin: 0;
  padding: 0;
}

@media (min-width: 640px) {
  .country-vacations-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 768px) {
  .country-vacations-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 639px) {
  .country-vacations-grid {
    grid-template-columns: 1fr;
  }
}

.country-vacation-card {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  border: 1px solid var(--gray-200);
  border-radius: 8px;
  background: none;
  cursor: pointer;
  text-align: left;
  transition: all 0.2s;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.country-vacation-card:hover {
  border-color: var(--theme);
  background-color: var(--theme-light);
}

.country-vacation-image-wrapper {
  flex-shrink: 0;
}

.country-vacation-image {
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 8px;
}

.country-vacation-image-placeholder {
  width: 80px;
  height: 80px;
  background-color: var(--gray-200);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.country-vacation-placeholder-icon {
  height: 40px;
  width: 40px;
  color: var(--gray-400);
}

.country-vacation-content {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.country-vacation-name {
  font-weight: 600;
  color: var(--gray-900);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 4px;
  max-width: 100%;
}

.country-vacation-title {
  font-size: 14px;
  color: var(--gray-600);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 8px;
  max-width: 100%;
}

.country-vacation-description {
  font-size: 12px;
  color: var(--gray-500);
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.4;
  max-height: 4.2em;
  margin-bottom: 8px;
}

.country-vacation-meta {
  display: flex;
  align-items: center;
  gap: 8px;
}

.country-vacation-badge {
  font-size: 12px;
  background-color: var(--theme-light);
  color: var(--theme);
  padding: 2px 8px;
  border-radius: 9999px;
}

.country-vacation-arrow-wrapper {
  flex-shrink: 0;
  align-self: center;
}

.country-vacation-arrow {
  height: 20px;
  width: 20px;
  color: var(--gray-400);
  transition: color 0.2s;
}

.country-vacation-card:hover .country-vacation-arrow {
  color: var(--theme);
}

/* Empty state */
.empty-state {
  text-align: center;
  padding: 48px 0;
}

.empty-state-icon {
  height: 64px;
  width: 64px;
  color: var(--gray-300);
  margin: 0 auto 16px;
}

.empty-state-title {
  font-size: 18px;
  font-weight: 500;
  color: var(--gray-900);
  margin-bottom: 8px;
}

.empty-state-text {
  color: var(--gray-500);
}

/* Map container */
.map-container {
  flex: 1;
  position: relative;
}

.map-element {
  width: 100%;
  height: 100%;
}

.map-loading {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--gray-100);
}

.loading-content {
  text-align: center;
}

.large-spinner {
  width: 48px;
  height: 48px;
  border: 2px solid transparent;
  border-bottom: 2px solid var(--theme);
  border-radius: 50%;
  margin: 0 auto 16px;
  animation: spin 1s linear infinite;
}

.loading-text {
  color: var(--gray-600);
}

/* Custom marker styles */
:deep(.custom-country-marker) {
  background: transparent !important;
  border: none !important;
  position: relative;
}

/* Map styles */
#destination-map {
  position: relative;
  z-index: 1;
}

/* Leaflet popup styling */
:deep(.leaflet-popup-content-wrapper) {
  border-radius: 8px !important;
  padding: 0 !important;
}

:deep(.leaflet-popup-content) {
  margin: 0 !important;
  padding: 0 !important;
}

:deep(.leaflet-popup-tip) {
  background: white !important;
}

/* Scroll styling */
::-webkit-scrollbar {
  width: 4px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
}

::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 2px;
}

::-webkit-scrollbar-thumb:hover {
  background: #a1a1a1;
}

.map-flag-container {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 2.5rem !important;
  height: 2.5rem !important;
  border: unset;
  background: unset;
  background-color: unset !important;
}

.map-modal .text-xl {
	 font-size: 1.8rem;
}
 .map-modal .h-10 {
	 height: 2.5rem;
}
 .map-modal .w-10 {
	 width: 2.5rem;
}
  
.search-filter-container {
  z-index: 1000;
}
</style>